import React from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

/**
 * Modern Pagination — drop-in replacement for the old Pagination component.
 * Props: totalItems, pageSize, currentPage, onPageChange
 */
const Pagination = ({ totalItems, pageSize, currentPage, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  if (totalPages <= 1) return null;

  // Build page list with ellipsis
  const delta = 1;
  const rawPages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= currentPage - delta && i <= currentPage + delta)
    ) {
      rawPages.push(i);
    }
  }

  const pages = [];
  let prev = null;
  for (const p of rawPages) {
    if (prev !== null && p - prev > 1) pages.push("…");
    pages.push(p);
    prev = p;
  }

  const btnBase = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 36,
    height: 36,
    padding: "0 10px",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    border: "1.5px solid #e2e8f0",
    background: "#fff",
    color: "#475569",
    cursor: "pointer",
    transition: "all .15s",
    fontFamily: "inherit",
    lineHeight: 1,
  };

  const btnActive = {
    ...btnBase,
    background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
    border: "none",
    color: "#fff",
    boxShadow: "0 4px 12px rgba(99,102,241,.3)",
  };

  const btnDisabled = {
    ...btnBase,
    color: "#cbd5e1",
    cursor: "not-allowed",
    opacity: 0.6,
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 6,
        marginTop: 24,
        flexWrap: "wrap",
        fontFamily: "'Inter','Segoe UI',sans-serif",
      }}
    >
      {/* Prev */}
      <button
        style={currentPage === 1 ? btnDisabled : btnBase}
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
        aria-label="Previous page"
      >
        <FaChevronLeft size={12} />
      </button>

      {/* Page numbers + ellipsis */}
      {pages.map((p, i) =>
        p === "…" ? (
          <span
            key={`ellipsis-${i}`}
            style={{ color: "#cbd5e1", padding: "0 4px", fontSize: 13, lineHeight: "36px" }}
          >
            …
          </span>
        ) : (
          <button
            key={p}
            style={p === currentPage ? btnActive : btnBase}
            onClick={() => onPageChange(p)}
            aria-label={`Page ${p}`}
            aria-current={p === currentPage ? "page" : undefined}
          >
            {p}
          </button>
        )
      )}

      {/* Next */}
      <button
        style={currentPage === totalPages ? btnDisabled : btnBase}
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
        aria-label="Next page"
      >
        <FaChevronRight size={12} />
      </button>
    </div>
  );
};

export default Pagination;
